"use client";

import React, { useRef, useState, useEffect } from "react";
import { GROQImage } from "@/sanity/lib/types";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import TestimonialSlider, { TestimonialSliderRef } from "./Testimonials";
import Image from "next/image";
import ChipSerif from "@/components/global/ui/ChipSerif";
import { Carousel, type CarouselApi, CarouselContent, CarouselItem } from "@/shadcn/ui/carousel";

import flagArrow from "@/public/home/flagArrow.svg";

interface TestimonialsSectionProps {
  testimonialsChip?: string;
  testimonialsTitle?: any[];
  testimonialsItems?: {
    country: string;
    flagIcon: GROQImage;
    quote: any[];
    name: string;
    rating: number;
  }[];
}

export default function TestimonialsSection({
  data,
  className,
  disableCurvature = false,
  futures = false,
}: {
  data: TestimonialsSectionProps;
  className?: string;
  disableCurvature?: boolean;
  futures?: boolean;
}) {
  const sliderRef = useRef<TestimonialSliderRef>(null);
  const flagsCarouselRef = useRef<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);

  if (!data?.testimonialsItems || data.testimonialsItems.length === 0) {
    return null;
  }

  const handleFlagClick = (index: number) => {
    sliderRef.current?.goToSlide(index);
  };

  // Duplicate flags for infinite looping
  const itemCount = data.testimonialsItems.length;
  const loopedFlags = [
    ...data.testimonialsItems,
    ...data.testimonialsItems,
    ...data.testimonialsItems,
  ];

  // Calculate fixed width in rem: (itemCount × flagWidth) + ((itemCount - 1) × gap)
  const flagWidthRem = 8.5;
  const gapRem = 4;
  const containerWidthRem = (itemCount * flagWidthRem) + ((itemCount - 1) * gapRem);

  // Sync flag carousel with active testimonial - scroll to middle set
  useEffect(() => {
    if (!flagsCarouselRef.current) return;
    const middleSetIndex = itemCount + activeIndex;
    flagsCarouselRef.current.scrollTo(middleSetIndex);
  }, [activeIndex, itemCount, flagsCarouselRef.current]);

  return (
    <section
      className={cn(
        "relative overflow-clip pt-[10rem] lg:pt-[21rem]",
        className
      )}
    >
      <div className="container relative flex flex-col items-center">
        <ChipSerif text={data?.testimonialsChip || ""} />

        {data?.testimonialsTitle && (
          <div className="richtext mx-auto mb-[3rem] lg:mb-[6rem]">
            <PortableText
              value={data.testimonialsTitle}
              components={richTextComponent}
            />
          </div>
        )}

        <Carousel
          setApi={(api) => {
            flagsCarouselRef.current = api;
            if (api) {
              // Initialize to middle set immediately when carousel is ready
              const middleSetIndex = itemCount;
              api.scrollTo(middleSetIndex, false);
            }
          }}
          opts={{
            align: "center",
            loop: false,
            skipSnaps: false,
            watchDrag: false,
            containScroll: false,
          }}
          className="max-w-full pb-[8rem] lg:pb-[10rem] [mask-image:linear-gradient(to_right,transparent_0%,black_15%,black_85%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_0%,black_15%,black_85%,transparent_100%)] [&_>_div]:!overflow-visible"
          style={{
            width: `${containerWidthRem}rem`
          }}
        >
          <CarouselContent
            className="-ml-[3rem] lg:-ml-[4rem] lg:mt-[.5rem]"
          >
            {loopedFlags.map((testimonial, loopIndex) => {
              const originalIndex = loopIndex % itemCount;
              const isInMiddleSet = loopIndex >= itemCount && loopIndex < itemCount * 2;
              const isActive = activeIndex === originalIndex && isInMiddleSet;

              return testimonial.flagIcon?.src && (
                <CarouselItem
                  key={loopIndex}
                  className="pl-[3rem] lg:pl-[4rem] basis-auto"
                >
                  <button
                    onClick={() => handleFlagClick(originalIndex)}
                    className="relative group cursor-pointer transition-all duration-300 flex flex-col items-center gap-[1rem]"
                    title={testimonial.country}
                  >
                    <div className={cn(
                      "w-[7rem] lg:w-[8.5rem] transition-all duration-300 group-hover:scale-125",
                      isActive && "scale-125"
                    )}>
                      <Image
                        src={{
                          src: testimonial.flagIcon.src,
                          width: testimonial.flagIcon.width,
                          height: testimonial.flagIcon.height,
                        }}
                        alt={testimonial.country}
                        className="!w-full"
                      />
                    </div>

                    {isActive && (
                      <div className="absolute top-[calc(100%+1.75rem)] left-1/2 -translate-x-1/2 flex flex-col items-center gap-[.5rem]">
                        <Image
                          className="!w-[1.625rem]"
                          src={flagArrow}
                          alt="arrow up"
                        />
                        <p className="mb-0 text-black text-[2rem] font-medium whitespace-nowrap leading-none">
                          {testimonial.country}
                        </p>
                      </div>
                    )}
                  </button>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      </div>

      <TestimonialSlider
        ref={sliderRef}
        testimonials={data.testimonialsItems}
        onActiveIndexChange={setActiveIndex}
        disableCurvature={disableCurvature}
        futures={futures}
      />
    </section>
  );
}
